import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Users, UserCog, LogOut, Sparkles, CalendarDays, HardHat, Settings, ChevronDown, BarChart3, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

type NavItem = { to: string; label: string; testid: string; admin?: boolean };
type NavGroup = { label: string; icon: any; testid: string; to?: string; admin?: boolean; installer?: boolean; children?: NavItem[] };

const NAV: NavGroup[] = [
  { label: "Homeowners", icon: Users, testid: "nav-homeowners", to: "/homeowners", children: [{ to: "/quotes", label: "Quotes", testid: "nav-quotes" }] },
  { label: "Team Members", icon: UserCog, testid: "nav-users", to: "/users", admin: true },
  { label: "Schedule", icon: CalendarDays, testid: "nav-schedule", children: [{ to: "/calendar", label: "Install Calendar", testid: "nav-calendar" }, { to: "/work-orders", label: "Work Orders", testid: "nav-work-orders" }, { to: "/inventory", label: "Materials & Inventory", testid: "nav-inventory", admin: true }] },
  { label: "Reports", icon: BarChart3, testid: "nav-reports", admin: true, children: [{ to: "/reports/owed", label: "Owed to Installers", testid: "nav-reports-owed", admin: true }] },
  { label: "Settings", icon: Settings, testid: "nav-settings", children: [{ to: "/settings/content", label: "Content", testid: "nav-content" }, { to: "/settings/images", label: "Backup pictures", testid: "nav-images" }, { to: "/settings/pricing", label: "Pricing", testid: "nav-pricing" }, { to: "/settings/checklists", label: "Checklists", testid: "nav-checklists" }, { to: "/settings/materials", label: "Materials", testid: "nav-materials" }, { to: "/settings/contracts", label: "Contracts", testid: "nav-contracts", admin: true }] },
  { label: "My Jobs", icon: HardHat, testid: "nav-my-jobs", to: "/my-jobs", installer: true },
];

const rowCls = (active: boolean) => cn("flex flex-1 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150", active ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-100");

function NavGroupRow({ g, isAdmin }: { g: NavGroup; isAdmin: boolean }) {
  const { pathname } = useLocation();
  const inGroup = (g.to ? pathname.startsWith(g.to) : false) || !!g.children?.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = useState(inGroup);
  const wasIn = useRef(inGroup);
  useEffect(() => { if (inGroup && !wasIn.current) setOpen(true); wasIn.current = inGroup; }, [inGroup]);
  const parentActive = g.to ? pathname.startsWith(g.to) : inGroup && !open;
  const chevron = g.children && <button type="button" data-testid={`${g.testid}-toggle`} aria-label={`Toggle ${g.label}`} aria-expanded={open} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen((o) => !o); }} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-200 hover:text-gray-900"><ChevronDown size={15} className={cn("transition-transform duration-200", open ? "rotate-180" : "")} /></button>;
  return (
    <div data-testid={`${g.testid}-group`}>
      <div className="flex items-center gap-1">
        {g.to ? (
          <NavLink to={g.to} data-testid={g.testid} className={rowCls(parentActive)}><g.icon size={18} /> {g.label}</NavLink>
        ) : (
          <button type="button" data-testid={g.testid} onClick={() => setOpen((o) => !o)} className={rowCls(parentActive)}><g.icon size={18} /> {g.label}</button>
        )}
        {chevron}
      </div>
      {g.children && open && (
        <div className="ml-5 mt-1 space-y-0.5 border-l border-gray-200 pl-3" data-testid={`${g.testid}-children`}>
          {g.children.filter((c) => !c.admin || isAdmin).map((c) => (
            <NavLink key={c.to} to={c.to} data-testid={c.testid}
              className={({ isActive }) => cn("block rounded-md px-3 py-2 text-[13px] font-medium transition-colors duration-150", isActive ? "bg-blue-50 text-blue-700" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900")}>
              {c.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppShell({ children, title, actions }: { children: React.ReactNode; title: string; actions?: React.ReactNode }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const u = user as any;
  const groups = NAV.filter((n) => u?.role === "installer" ? n.installer : !n.installer && (!n.admin || u?.role === "admin"));
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => { document.body.style.overflow = menuOpen ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [menuOpen]);
  return (
    <div className="min-h-screen bg-gray-50">
      {menuOpen && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" data-testid="mobile-nav-overlay" onClick={() => setMenuOpen(false)} />}
      <aside className={cn("fixed inset-y-0 left-0 z-50 w-64 border-r border-gray-200 bg-white transition-transform duration-200 ease-out lg:translate-x-0", menuOpen ? "translate-x-0 shadow-2xl" : "-translate-x-full")} data-testid="sidebar" aria-hidden={!menuOpen ? undefined : false}>
        <div className="flex h-16 items-center gap-2.5 border-b border-gray-200 px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-700 text-white"><Sparkles size={16} /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-bold leading-none text-gray-900">StarLites BusOps</div>
            <div className="text-[11px] text-gray-500">Quotes · Work Orders · Installs</div>
          </div>
          <button type="button" data-testid="mobile-nav-close" aria-label="Close menu" onClick={() => setMenuOpen(false)} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 lg:hidden"><X size={18} /></button>
        </div>
        <nav className="max-h-[calc(100vh-9.5rem)] space-y-1 overflow-y-auto p-3" data-testid="main-nav">
          {groups.map((g) => <NavGroupRow key={g.label} g={g} isAdmin={u?.role === "admin"} />)}
        </nav>
        <div className="absolute inset-x-0 bottom-0 border-t border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <button type="button" data-testid="user-menu-btn" onClick={() => nav("/account")} title="Account & Users" className="flex min-w-0 flex-1 items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors hover:bg-gray-100">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 text-sm font-semibold text-blue-700">{u?.name?.[0]?.toUpperCase()}</div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-gray-900" data-testid="current-user-name">{u?.name}</div>
                <div className="truncate text-xs capitalize text-gray-500">{u?.is_super ? "Super user" : u?.role} · <span className="normal-case text-blue-700">Account</span></div>
              </div>
            </button>
            <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900" title="Sign out"><LogOut size={16} /></button>
          </div>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex min-h-16 flex-wrap items-center justify-between gap-x-3 gap-y-2 border-b border-gray-200 bg-white px-3 py-2 sm:px-6 lg:px-8">
          <div className="flex min-w-0 items-center gap-2">
            <button type="button" data-testid="mobile-nav-toggle" aria-label="Open menu" onClick={() => setMenuOpen(true)} className="rounded-md p-2 text-gray-600 hover:bg-gray-100 lg:hidden"><Menu size={20} /></button>
            <h1 className="truncate text-lg font-semibold text-gray-900 sm:text-xl" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex max-w-full flex-wrap items-center justify-end gap-2">{actions}</div>
        </header>
        <main className="p-3 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
